<template>
  <main class="rh-home">
    <header class="hero">
      <div class="hero-meta">
        <span class="company-chip">{{ company.toUpperCase() }}</span>
        <span class="meta-dot">&gt;</span>
        <span class="meta-text">RH</span>
      </div>
      <p class="eyebrow">RH</p>
      <h1>Home de Recrutamento e Seleção</h1>
      <p class="description">
        Escolha a ação desejada para este módulo.
      </p>
    </header>

    <section class="grid">
      <article v-for="card in cards" :key="card.id" class="card">
        <img class="card-icon" :src="card.icon" :alt="card.iconAlt">
        <h2>{{ card.title }}</h2>
        <p>{{ card.description }}</p>
        <router-link class="card-link" :to="card.to">
          {{ card.cta }}
        </router-link>
      </article>
    </section>

    <footer class="actions">
      <router-link class="link" :to="`/${company}`">Voltar para home da empresa</router-link>
      <router-link class="link" :to="`/${company}/grc`">Ir para GRC</router-link>
    </footer>
  </main>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import processosIcon from '@/assets/all-users_icon.png';
import novoProcessoIcon from '@/assets/add-pesquisa_icon.png';
import indicadoresIcon from '@/assets/bar-chart_icon.png';

const route = useRoute();

const normalizeSlug = (value) => (value || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const company = computed(() => normalizeSlug(route.params.company) || 'due');

const cards = computed(() => [
  {
    id: 'processos',
    title: 'Processos seletivos',
    description: 'Acompanhe os processos já criados e suas entrevistas.',
    cta: 'Abrir processos',
    to: `/${company.value}/eia/recrutamento/processos`,
    icon: processosIcon,
    iconAlt: 'Ícone de processos seletivos',
  },
  {
    id: 'novo',
    title: 'Novo processo',
    description: 'Cadastre e publique um novo processo seletivo.',
    cta: 'Abrir novo processo',
    to: `/${company.value}/eia/recrutamento/form-pesquisa`,
    icon: novoProcessoIcon,
    iconAlt: 'Ícone de novo processo',
  },
  {
    id: 'indicadores',
    title: 'Indicadores',
    description: 'Visualize indicadores do módulo de recrutamento.',
    cta: 'Ver indicadores',
    to: `/${company.value}/eia/recrutamento/indicadores`,
    icon: indicadoresIcon,
    iconAlt: 'Ícone de indicadores',
  },
]);
</script>

<style scoped>
.rh-home {
  max-width: 1024px;
  margin: 0 auto;
  padding: 32px 20px 48px;
  color: #0f172a;
  position: relative;
}

.rh-home::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 220px;
  background: linear-gradient(135deg, rgba(79, 70, 229, 0.14), rgba(15, 23, 42, 0.06));
  border-radius: 0 0 28px 28px;
  z-index: 0;
}

.hero {
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}

.hero-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-size: 12px;
  margin-bottom: 10px;
}

.company-chip {
  color: #0f172a;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.meta-dot {
  color: #64748b;
}

.meta-text {
  color: #475569;
}

.eyebrow {
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0369a1;
}

h1 {
  margin: 0 0 8px;
  font-size: 32px;
  line-height: 1.2;
}

.description {
  margin: 0;
  font-size: 16px;
  color: #334155;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
  position: relative;
  z-index: 1;
}

.card {
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  background: #f8fafc;
  box-shadow: 0 12px 24px rgba(10, 34, 43, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 248px;
}

.card:hover,
.card:focus-within {
  transform: translateY(-4px);
  box-shadow: 0 16px 30px rgba(10, 34, 43, 0.12);
}

.card-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
  margin-bottom: 10px;
  transition: transform 0.2s ease;
}

.card:hover .card-icon,
.card:focus-within .card-icon {
  transform: scale(1.05);
}

.card h2 {
  margin: 0 0 8px;
  font-size: 18px;
}

.card p {
  margin: 0 0 12px;
  color: #334155;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
  min-height: 46px;
}

.card-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #0f172a;
  border-radius: 8px;
  padding: 8px 12px;
  text-decoration: none;
  color: #0f172a;
  font-weight: 600;
  background: #ffffff;
}

.card-link:hover {
  background: #f1f5f9;
}

.card-link:focus-visible {
  outline: 2px solid #0f172a;
  outline-offset: 2px;
  background: #f1f5f9;
}

.actions {
  display: flex;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.link {
  color: #0369a1;
  font-weight: 600;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}
</style>
